import {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
  useRef,
} from "react";
import Column from "../../../../components/Column";
import Text from "../../../../components/Text";
import styled from "styled-components";
import { styles } from "../../../../styles/themes/style";
import { useFeedsHandlers } from "../../../../services/actions/feeds/useFeedsHandlers";
import { useFeedsStore } from "../../../../stores/feeds/useFeedsStore";
import { useAppHandlers } from "../../../../services/actions/app/useAppHandlers";
import { IFeed } from "../../../../interfaces/models/feed";
import { IConnection } from "../../../../interfaces/models/connection";
import Checkbox from "../../../../components/Checkbox";
import ProfilePhoto from "../../../../components/ProfilePhoto";
import { capitalLetters } from "../../../../utils";
import Button from "../../../../components/Button";
import Table from "../../../../components/Table";
import useMediaQuery from "../../../../components/useMediaQuery";
import ConnectionActionWithFeedsDropdown from "../../../../fragments/dropdowns/ConnectionActionWithFeedsDropdown";
import EditDeleteActionDropdown from "../../../../fragments/dropdowns/EditDeleteActionDropdown";
import { adjustedColumnsHandler } from "../../../../utils";
import EmptyPage from "../../../../components/EmptyPage";
import ActionText from "../../../../components/ActionText";
import Row from "../../../../components/Row";
import SelectAllCheckbox from "../../../../components/SelectAllCheckbox";
import TabsView, { ITabProperties } from "../../../../components/TabsView";
import InputNumber from "../../../../components/InputNumber";
import { usePostsHandlers } from "../../../../services/actions/posts/usePostsHandlers";
import SvgFragment from "../../../../fragments/SvgFragment";

interface IFeedManagementContentProps {}

const FeedManagementContent: FunctionComponent<
  IFeedManagementContentProps
> = () => {
  const [selectedSlug, setSelectedSlug] = useState<string>("manageFeeds");
  const [commentsThisWeekCount, setCommentsThisWeekCount] = useState<number>(0);
  const [commentsWeeklyGoal, setCommentsWeeklyGoal] = useState<number>(100);
  const { getFeedsHandler } = useFeedsHandlers();

  const feedTabs: ITabProperties[] = [
    {
      slug: "manageFeeds",
      title: "Manage feeds",
      selected: true,
    },
    {
      slug: "engagementProgress",
      title: "Engagement progress",
      selected: false,
    },
  ];

  const [tabs, setTabs] = useState<ITabProperties[]>(feedTabs);

  const switchTabs = useCallback(
    (slug: string) => {
      setSelectedSlug(slug);
      setTabs(tabs.map((tab) => ({ ...tab, selected: tab.slug === slug })));
    },
    [tabs]
  );

  // Load progress data when component mounts
  useEffect(() => {
    getFeedsHandler().then((result) => {
      if (result && result.commentsThisWeekCount !== undefined) {
        setCommentsThisWeekCount(result.commentsThisWeekCount);
      }
      if (result && result.commentsWeeklyGoal !== undefined) {
        setCommentsWeeklyGoal(result.commentsWeeklyGoal);
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const renderComponent: { [key: string]: React.ReactNode } = {
    manageFeeds: <ManageFeedsTab />,
    engagementProgress: (
      <EngagementProgressTab
        commentsThisWeekCount={commentsThisWeekCount}
        commentsWeeklyGoal={commentsWeeklyGoal}
        setCommentsWeeklyGoal={setCommentsWeeklyGoal}
      />
    ),
  };

  return (
    <Container>
      <TabsView onSwitchTab={(slug) => switchTabs(slug)} tabs={tabs} />
      {renderComponent[selectedSlug]}
    </Container>
  );
};

const ManageFeedsTab: FunctionComponent = () => {
  const {
    getFeedsHandler,
    deleteFeedHandler,
    renameFeedHandler,
    getFeedConnectionsHandler,
    bulkRemoveConnectionsFromFeedHandler,
  } = useFeedsHandlers();
  const {
    setDestructiveModal,
    hideModal,
    setRenameFeedModal,
    setCreateFeedModal,
  } = useAppHandlers();
  const {
    store: { feeds, loading },
  } = useFeedsStore();
  const tableRef = useRef<HTMLDivElement>(null);
  const isSmall = useMediaQuery("(max-width: 768px)");
  const isMedium = useMediaQuery("(max-width: 1240px)");
  const [showConnections, setShowConnections] = useState<boolean>(false);
  const [selectedFeed, setSelectedFeed] = useState<IFeed | null>(null);
  const [feedConnections, setFeedConnections] = useState<IConnection[]>([]);
  const [connectionsLoading, setConnectionsLoading] = useState<boolean>(false);
  const [selectedConnections, setSelectedConnections] = useState<string[]>([]);
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);

  useEffect(() => {
    const fetchFeeds = async () => {
      try {
        await getFeedsHandler();
      } catch (error) {
        console.error("Error fetching feeds:", error);
      }
    };
    fetchFeeds();

    // Set current section identifier
    sessionStorage.setItem("lastSection", "settings");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear localStorage when component unmounts (navigating away from settings)
  useEffect(() => {
    return () => {
      // Cleanup when navigating away from Settings
      localStorage.removeItem("settingsSection_showConnections");
      localStorage.removeItem("settingsSection_selectedFeedId");
    };
  }, []);

  // Initialize state from localStorage
  useEffect(() => {
    const showConnectionsParam =
      localStorage.getItem("settingsSection_showConnections") === "true";
    const selectedFeedId = localStorage.getItem(
      "settingsSection_selectedFeedId"
    );

    if (showConnectionsParam && selectedFeedId && feeds.length > 0) {
      const feed = feeds.find((f: IFeed) => f._id === selectedFeedId);
      if (feed) {
        setSelectedFeed(feed);
        setShowConnections(true);
        // Load connections for the selected feed
        setConnectionsLoading(true);
        getFeedConnectionsHandler(feed._id)
          .then((result) => {
            setFeedConnections(result?.connections || []);
            setConnectionsLoading(false);
          })
          .catch((error) => {
            console.error("Error fetching feed connections:", error);
            setConnectionsLoading(false);
          });
      }
    }
  }, [feeds]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRename = useCallback(
    (feed: IFeed) => {
      setRenameFeedModal(feed.name, async (newName: string) => {
        try {
          await renameFeedHandler(feed._id, newName);
        } catch (error) {
          console.error("Error renaming feed:", error);
        }
      });
    },
    [setRenameFeedModal, renameFeedHandler]
  );

  const handleCreateFeed = useCallback(() => {
    setCreateFeedModal([], true);
  }, [setCreateFeedModal]);

  const handleFeedClick = useCallback(
    async (feed: IFeed) => {
      setSelectedFeed(feed);
      setConnectionsLoading(true);
      setShowConnections(true);
      setSelectedConnections([]);
      setIsAllSelected(false);

      // Save to localStorage
      localStorage.setItem("settingsSection_showConnections", "true");
      localStorage.setItem("settingsSection_selectedFeedId", feed._id);

      try {
        const result = await getFeedConnectionsHandler(feed._id);
        setFeedConnections(result?.connections || []);
      } catch (error) {
        console.error("Error fetching feed connections:", error);
      } finally {
        setConnectionsLoading(false);
      }
    },
    [getFeedConnectionsHandler]
  );

  const handleBackToFeeds = useCallback(() => {
    setShowConnections(false);
    setSelectedFeed(null);
    setFeedConnections([]);
    setSelectedConnections([]);
    setIsAllSelected(false);

    // Remove from localStorage
    localStorage.removeItem("settingsSection_showConnections");
    localStorage.removeItem("settingsSection_selectedFeedId");
  }, []);

  const handleConnectionSelect = useCallback((connectionId: string) => {
    setSelectedConnections((prev) =>
      prev.includes(connectionId)
        ? prev.filter((id) => id !== connectionId)
        : [...prev, connectionId]
    );
  }, []);

  const selectAllCheckboxHandler = useCallback(() => {
    const selectedIds = !isAllSelected
      ? feedConnections.map((conn) => conn._id)
      : [];
    setIsAllSelected(!isAllSelected);
    setSelectedConnections(selectedIds);
  }, [isAllSelected, feedConnections]);

  const selectOnThisPageHandler = useCallback(() => {
    const newConnections = feedConnections.filter(
      (connection) => !selectedConnections.includes(connection._id)
    );
    setIsAllSelected(false);
    setSelectedConnections([
      ...selectedConnections,
      ...newConnections.map((conn) => conn._id),
    ]);
  }, [feedConnections, selectedConnections]);

  const clearHandler = useCallback(() => {
    setIsAllSelected(false);
    setSelectedConnections([]);
  }, []);

  const handleRemoveFromFeed = useCallback(() => {
    if (!selectedFeed || selectedConnections.length === 0) return;

    setDestructiveModal({
      headingText: "Remove from feed",
      descriptionText: `Are you sure you want to remove ${
        selectedConnections.length
      } connection${selectedConnections.length > 1 ? "s" : ""} from this feed?`,
      confirmBtnTitle: "Remove",
      onCancelHandler: () => hideModal(),
      onConfirmHandler: async () => {
        hideModal();
        try {
          const result = await bulkRemoveConnectionsFromFeedHandler(
            selectedFeed._id,
            selectedConnections
          );
          if (result?.success) {
            // Refresh feed connections
            const updatedResult = await getFeedConnectionsHandler(
              selectedFeed._id
            );
            setFeedConnections(updatedResult?.connections || []);
            setSelectedConnections([]);
          }
        } catch (error) {
          console.error("Error removing connections from feed:", error);
        }
      },
    });
  }, [
    selectedConnections,
    selectedFeed,
    setDestructiveModal,
    hideModal,
    bulkRemoveConnectionsFromFeedHandler,
    getFeedConnectionsHandler,
  ]);

  const handleRemoveSingleConnectionFromFeed = useCallback(
    (connectionId: string) => {
      if (!selectedFeed) return;

      const connection = feedConnections.find(
        (conn) => conn._id === connectionId
      );
      const connectionName = connection
        ? `${connection.firstName} ${connection.lastName}`
        : "this connection";

      setDestructiveModal({
        headingText: "Remove from feed",
        descriptionText: `Are you sure you want to remove ${connectionName} from this feed?`,
        onCancelHandler: () => hideModal(),
        confirmBtnTitle: "Remove",
        onConfirmHandler: async () => {
          hideModal();
          try {
            const result = await bulkRemoveConnectionsFromFeedHandler(
              selectedFeed._id,
              [connectionId]
            );
            if (result?.success) {
              // Refresh feed connections
              const updatedResult = await getFeedConnectionsHandler(
                selectedFeed._id
              );
              setFeedConnections(updatedResult?.connections || []);
            }
          } catch (error) {
            console.error("Error removing connection from feed:", error);
          }
        },
      });
    },
    [
      selectedFeed,
      feedConnections,
      setDestructiveModal,
      hideModal,
      bulkRemoveConnectionsFromFeedHandler,
      getFeedConnectionsHandler,
    ]
  );

  const handleDelete = useCallback(
    (feedId: string) => {
      setDestructiveModal({
        headingText: "Delete feed",
        descriptionText: "Are you sure you want to delete this feed?",
        onCancelHandler: () => hideModal(),
        onConfirmHandler: () => {
          hideModal();
          deleteFeedHandler(feedId);
        },
      });
    },
    [setDestructiveModal, hideModal, deleteFeedHandler]
  );

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const sortHandler = (props: any) => {
    const adjustedColumns = adjustedColumnsHandler(
      columnsInitialState,
      columns,
      tableRef,
      isSmall,
      isMedium,
      sortHandler
    );
    const selectedColumnIndex = columns.findIndex(
      (column) => column.key === props.key
    );
    if (selectedColumnIndex !== -1) {
      props.sort = props.sort === "" || props.sort === "za" ? "az" : "za";
      adjustedColumns[selectedColumnIndex] = props;
      setColumns(adjustedColumns);
    }
  };

  const columnsInitialState = [
    {
      header: "Name",
      key: ["name"],
      show: true,
      sort: "",
      sortHandler: (e: any) => sortHandler(e),
      showSmall: true,
    },
    {
      header: "Contacts",
      key: ["numConnections"],
      show: true,
      showSmall: true,
    },
    { header: "Date Created", key: ["createdAt"], show: true, showSmall: true },
    { header: "Actions", key: ["actions"], show: true, showSmall: true },
  ];

  const connectionsColumnsInitialState = [
    {
      header: "Name",
      key: ["name"],
      show: true,
      sort: "",
      sortHandler: (e: any) => sortHandler(e),
      showSmall: true,
      width: "250px",
    },
    {
      header: "Email",
      key: ["email"],
      show: true,
      showSmall: true,
      width: "300px",
    },
    { header: "Date Added", key: ["dateAdded"], show: true, showSmall: true },
    { header: "Actions", key: ["actions"], show: true, showSmall: true },
  ];

  const [columns, setColumns] = useState(columnsInitialState);
  const [connectionsColumns, setConnectionsColumns] = useState(
    connectionsColumnsInitialState
  );

  useEffect(() => {
    const adjustedColumns = adjustedColumnsHandler(
      columnsInitialState,
      columns,
      tableRef,
      isSmall,
      isMedium,
      sortHandler
    );
    setColumns(adjustedColumns);
  }, [isSmall, isMedium]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const adjustedConnectionsColumns = adjustedColumnsHandler(
      connectionsColumnsInitialState,
      connectionsColumns,
      tableRef,
      isSmall,
      isMedium,
      sortHandler
    );

    // Add renderHeaderCell to the first column (Name column)
    if (adjustedConnectionsColumns.length > 0) {
      adjustedConnectionsColumns[0].renderHeaderCell = () => (
        <Row gap="8px" alignItems>
          <SelectAllCheckbox
            isCheckboxChecked={
              isAllSelected ||
              (selectedConnections.length > 0 &&
                selectedConnections.length === feedConnections.length)
            }
            pageSize={feedConnections.length}
            total={feedConnections.length}
            paginated={feedConnections.length}
            isAllSelected={isAllSelected}
            selected={selectedConnections.length}
            label={"contacts"}
            selectAllHandler={selectAllCheckboxHandler}
            selectOnThisPageHadler={selectOnThisPageHandler}
            clearHandler={clearHandler}
            backgroundColor="white"
          />
          {selectedConnections.length === 0 && (
            <Text $lighter $heading6>
              Name
            </Text>
          )}
        </Row>
      );
    }

    setConnectionsColumns(adjustedConnectionsColumns);
  }, [
    isSmall,
    isMedium,
    isAllSelected,
    selectedConnections,
    feedConnections,
    selectAllCheckboxHandler,
    selectOnThisPageHandler,
    clearHandler,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  if (showConnections) {
    return (
      <TabContainer>
        <FixedHeader>
          <BackSection>
            <ActionText icon="arrowLeft" onClickHandler={handleBackToFeeds}>
              Back to Manage feeds
            </ActionText>
          </BackSection>
          <TitleSection>
            <Row gap="12px">
              <Column gap="12px" spaceBetween>
                <Text $heading4>{selectedFeed?.name}</Text>
              </Column>
            </Row>
          </TitleSection>
          <ActionSection>
            <Button
              $type="blue"
              $bigButton
              $customDisabled={styles.colors.primary200}
              onClickHandler={handleRemoveFromFeed}
              disabled={selectedConnections.length === 0}
            >
              Remove from feed
            </Button>
          </ActionSection>
        </FixedHeader>

        <ScrollableTableContainer>
          <Table
            ref={tableRef}
            pageLoading={connectionsLoading ? 5 : undefined}
            noDataPage={
              <EmptyPage
                svgType="feedEmptyState"
                title="No contacts in this feed"
                label="Add contacts and start engaging"
              />
            }
            columns={connectionsColumns}
            data={(feedConnections || []).map((connection: IConnection) => ({
              object: { ...connection, borderTop: "#e0e0e0 1px solid" },
              name: (
                <Row gap="8px" alignItems>
                  <Checkbox
                    checked={selectedConnections.includes(connection._id)}
                    onClickHandler={() =>
                      handleConnectionSelect(connection._id)
                    }
                    checkboxId={connection._id}
                  />
                  <ProfilePhoto
                    tooltipMessage={`${connection.firstName} ${connection.lastName}`}
                    capitalLetters={capitalLetters(connection)}
                    source={connection.profilePicture}
                  />
                  <Text
                    $bold
                  >{`${connection.firstName} ${connection.lastName}`}</Text>
                </Row>
              ),
              email: <Text>{connection.email || ""}</Text>,
              dateAdded: (
                <Text>
                  {(() => {
                    const feedData = connection.feeds?.find(
                      (feed) => feed._id === selectedFeed?._id
                    );
                    return feedData?.dateAdded
                      ? formatDate(feedData.dateAdded)
                      : "";
                  })()}
                </Text>
              ),
              actions: (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <ConnectionActionWithFeedsDropdown
                    connection={connection}
                    currentFeedId={selectedFeed?._id}
                    onUpdate={async () => {
                      // Refresh feed connections when feeds are updated
                      const updatedResult = await getFeedConnectionsHandler(
                        selectedFeed!._id
                      );
                      setFeedConnections(updatedResult?.connections || []);
                    }}
                    onCreateFeed={(connectionId) => {
                      // Open create feed modal with selected connection
                      setCreateFeedModal([connectionId], true);
                    }}
                  />
                </div>
              ),
            }))}
          />
        </ScrollableTableContainer>
      </TabContainer>
    );
  }

  return (
    <TabContainer>
      <FixedHeader>
        <TitleSection>
          <Row gap="12px" spaceBetween alignItems>
            <Column gap="12px" spaceBetween>
              <Text $heading3>Manage feeds</Text>
              <Text $paragraph4>
                Create feeds based on people you want to engage with. Comment
                and react when they post to LinkedIn. We suggest creating
                separate feeds for categories such as target prospects,
                existing customers, competitors, and influencers.
              </Text>
            </Column>
            <Column gap="12px">
              <Button
                $type="blue"
                $bigButton
                onClickHandler={handleCreateFeed}
              >
                Create new feed
              </Button>
            </Column>
          </Row>
        </TitleSection>
      </FixedHeader>

      <ScrollableTableContainer>
        <Table
          ref={tableRef}
          pageLoading={loading ? 10 : undefined}
          noDataPage={
            <EmptyPage
              svgType="feedEmptyState"
              title="No feeds created yet"
              label="Create your first feed, add contacts whose posts you want to track and start engaging"
            />
          }
          columns={columns}
          $noGap={true}
          data={(feeds || []).map((feed: IFeed) => ({
            object: { ...feed, borderTop: "#e0e0e0 1px solid" },
            onRowClick: (feed: IFeed) => handleFeedClick(feed),
            name: <Text>{feed.name}</Text>,
            numConnections: <Text>{feed.numConnections || 0}</Text>,
            createdAt: <Text>{formatDate(feed.createdAt)}</Text>,
            actions: (
              <div
                style={{ display: "flex", justifyContent: "flex-end" }}
                onClick={(e: any) => e.stopPropagation()}
              >
                <EditDeleteActionDropdown
                  object={feed}
                  type="feed"
                  title="Rename"
                  deleteTitle="Delete"
                  onDeleteHandler={() => handleDelete(feed._id)}
                  onEditHandler={() => handleRename(feed)}
                />
              </div>
            ),
          }))}
        />
      </ScrollableTableContainer>
    </TabContainer>
  );
};

const FeedManagement: FunctionComponent = () => {
  return <FeedManagementContent />;
};

const Container = styled.div`
  width: 100%;
  height: 574px;
  max-height: 574px;
  display: flex;
  flex-direction: column;
  padding: 8px;
  gap: 12px;
`;

const TabContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 24px;
  overflow-y: auto;
`;

const FixedHeader = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ScrollableTableContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0; /* Important for flex child to be scrollable */
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const BackSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;

  span {
    color: ${styles.colors.black400};
  }

  svg {
    path {
      fill: ${styles.colors.primary600};
    }
  }
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const ActionSection = styled.div`
  display: flex;
  justify-content: flex-end;
`;

// EngagementProgressTab component
interface IEngagementProgressTabProps {
  commentsThisWeekCount: number;
  commentsWeeklyGoal: number;
  setCommentsWeeklyGoal: (goal: number) => void;
}

const EngagementProgressTab: FunctionComponent<IEngagementProgressTabProps> = ({
  commentsThisWeekCount,
  commentsWeeklyGoal,
  setCommentsWeeklyGoal,
}) => {
  const { updateWeeklyGoalHandler } = usePostsHandlers();
  const { getFeedsHandler } = useFeedsHandlers();
  const [debouncedGoal, setDebouncedGoal] =
    useState<number>(commentsWeeklyGoal);

  useEffect(() => {
    // Load progress data when component mounts
    getFeedsHandler().then((result) => {
      if (result && result.commentsThisWeekCount !== undefined) {
        // This would be handled by parent component
      }
      if (result && result.commentsWeeklyGoal !== undefined) {
        setCommentsWeeklyGoal(result.commentsWeeklyGoal);
        setDebouncedGoal(result.commentsWeeklyGoal);
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounce effect for API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      if (debouncedGoal !== commentsWeeklyGoal) {
        handleWeeklyGoalChange(debouncedGoal);
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [debouncedGoal]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWeeklyGoalChange = useCallback(
    async (newGoal: number) => {
      try {
        const result = await updateWeeklyGoalHandler(newGoal);
        if (result?.success) {
          setCommentsWeeklyGoal(newGoal);
        } else {
          // Reset input to previous value if API call failed
          setDebouncedGoal(commentsWeeklyGoal);
        }
      } catch (error) {
        console.error("Error updating weekly goal:", error);
        // Reset input to previous value if API call failed
        setDebouncedGoal(commentsWeeklyGoal);
      }
    },
    [updateWeeklyGoalHandler, setCommentsWeeklyGoal, commentsWeeklyGoal]
  );

  const handleInputChange = useCallback((newGoal: number) => {
    setDebouncedGoal(newGoal);
  }, []);

  const progressPercentage = Math.min(
    (commentsThisWeekCount / commentsWeeklyGoal) * 100,
    100
  );
  const commentsRemaining = Math.max(
    commentsWeeklyGoal - commentsThisWeekCount,
    0
  );

  return (
    <EngagementProgressContainer>
      <Column gap="32px">
        <ProgressCard>
          <ProgressHeader>
            <Row gap="8px" alignItems>
              <SvgFragment type="cup" />
              <Text $heading4>Weekly comments goal</Text>
            </Row>
            <InputNumber
              prefilledValue={debouncedGoal}
              minValue={1}
              maxValue={1000}
              onChangeHandler={handleInputChange}
            />
          </ProgressHeader>

          <ProgressContent>
            <Row gap="12px" alignItems spaceBetween>
              <Text $paragraph2>Weekly Comments</Text>
              <Text $paragraph3>
                {commentsThisWeekCount}/{commentsWeeklyGoal}
              </Text>
            </Row>
            <ProgressBarContainer>
              <ProgressBarFill width={`${progressPercentage}%`} />
            </ProgressBarContainer>
          </ProgressContent>
        </ProgressCard>

        <StatsContainer>
          <StatCard>
            <StatValue>{Math.round(progressPercentage)}%</StatValue>
            <Text $paragraph1>Progress</Text>
          </StatCard>
          <StatCard>
            <StatValue>{commentsThisWeekCount}</StatValue>
            <Text $paragraph1>Comment left</Text>
          </StatCard>
          <StatCard>
            <StatValue>{commentsRemaining}</StatValue>
            <Text $paragraph1>Comment remaining</Text>
          </StatCard>
        </StatsContainer>
      </Column>
    </EngagementProgressContainer>
  );
};

const EngagementProgressContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding-top: 20px;
`;

const ProgressCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0px 1px 2px 0px #0000000d;
`;

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ProgressContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 8px;
  background: ${styles.colors.primary100};
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressBarFill = styled.div<{ width: string }>`
  height: 100%;
  width: ${(props) => props.width};
  background: ${styles.colors.primary300};
  border-radius: 4px;
  transition: width 0.3s ease;
`;

const RightAlignedText = styled(Text)`
  text-align: right;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  margin-bottom: 32px;
`;

const StatCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: white;
  border-radius: 8px;
  padding: 24px;
  text-align: center;
  box-shadow: 0px 0px 12px 0px #3339511a;
`;

const StatValue = styled(Text)`
  font-size: 28px;
  font-weight: bold;
  color: ${styles.colors.primary600};
  margin-bottom: 8px;
`;

export default FeedManagement;
